import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CheckCircle, Eye, EyeOff } from 'lucide-react';

const APP_ICON = "https://static.prod-images.emergentagent.com/jobs/3c404c7b-5045-4d45-8ddd-9111c2fbc8c7/images/1edd49db85dba9cc04f46bd52929381e881d2b7dac026dd5c68dd6bec34e756c.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== passwordConfirm) { setError('Adgangskoderne matcher ikke'); return; }
    if (password.length < 6) { setError('Adgangskoden skal være mindst 6 tegn'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password, password_confirm: passwordConfirm });
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#F9F8F6]">
        <Card className="w-full max-w-md border-[#EAE6DF]">
          <CardContent className="pt-6 text-center">
            <p className="text-[#5C706A] font-sans">Ugyldigt nulstillingslink.</p>
            <Link to="/login"><Button className="mt-4 bg-[#7F9C82] hover:bg-[#658368] font-sans">Gå til login</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F9F8F6]" data-testid="reset-password-page">
      <Card className="w-full max-w-md border-[#EAE6DF] shadow-sm">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-3">
            <img src={APP_ICON} className="w-14 h-14 rounded-xl" alt="Logo" />
          </div>
          <CardTitle className="text-3xl text-[#2F3E3B] tracking-tight">Ny adgangskode</CardTitle>
          <CardDescription className="text-[#5C706A] font-sans">Indtast din nye adgangskode herunder</CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#7F9C82]/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-[#7F9C82]" />
              </div>
              <p className="text-[#5C706A] font-sans text-sm">Din adgangskode er nulstillet.</p>
              <Link to="/login">
                <Button className="bg-[#7F9C82] hover:bg-[#658368] font-sans" data-testid="goto-login-button">Gå til login</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm font-sans">{error}</div>}
              <div className="space-y-1.5">
                <Label className="font-sans text-[#2F3E3B]">Ny adgangskode</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Mindst 6 tegn" required className="border-[#EAE6DF] pr-10" data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C706A]">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="font-sans text-[#2F3E3B]">Bekræft ny adgangskode</Label>
                <Input type={showPassword ? 'text' : 'password'} value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)}
                  placeholder="Gentag adgangskode" required className="border-[#EAE6DF]" data-testid="reset-password-confirm-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#7F9C82] hover:bg-[#658368] text-white font-sans"
                data-testid="reset-submit-button">
                {loading ? 'Nulstiller...' : 'Nulstil adgangskode'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
